/**
 * Primary visualization: Growth of AI agents over time
 */

import React from 'react'

interface YearDataPoint {
  year: number
  agents: number
  ai: number
  percentage: number
}

interface GrowthChartProps {
  yearData: Record<number, { total: number; agents: number; ai: number }>
  yearRange: [number, number]
}

export const GrowthChart: React.FC<GrowthChartProps> = ({
  yearData,
  yearRange,
}) => {
  // Convert to array and calculate percentages (agents / all YC companies)
  const dataPoints: YearDataPoint[] = []
  for (let year = yearRange[0]; year <= yearRange[1]; year++) {
    const data = yearData[year]
    if (data && data.total > 0) {
      dataPoints.push({
        year,
        agents: data.agents,
        ai: data.ai,
        percentage: (data.agents / data.total) * 100,
      })
    }
  }

  if (dataPoints.length === 0) {
    return (
      <div className="h-64 flex items-center justify-center text-secondary">
        No data available for selected range
      </div>
    )
  }

  const maxPercentage = Math.max(...dataPoints.map((d) => d.percentage))
  const chartHeight = 240
  const chartWidth = 600
  const padding = { top: 30, right: 30, bottom: 40, left: 50 }

  // Calculate positions
  const usableWidth = chartWidth - padding.left - padding.right
  const xStep = usableWidth / (dataPoints.length - 1 || 1)
  const yScale = (chartHeight - padding.top - padding.bottom) / (maxPercentage * 1.15)

  // Create path for the line
  const points = dataPoints.map((d, i) => ({
    x: padding.left + i * xStep,
    y: chartHeight - padding.bottom - d.percentage * yScale,
    ...d,
  }))

  const linePath = points
    .map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`)
    .join(' ')

  // Create area path
  const areaPath = `${linePath} L ${points[points.length - 1].x} ${chartHeight - padding.bottom} L ${padding.left} ${chartHeight - padding.bottom} Z`

  // Grid line values
  const gridLines = [0, 25, 50, 75].filter((pct) => pct <= maxPercentage * 1.15)

  return (
    <div className="w-full h-full min-h-[200px]">
      <svg
        viewBox={`0 0 ${chartWidth} ${chartHeight}`}
        className="w-full h-full"
        preserveAspectRatio="xMidYMid meet"
      >
        {/* Grid lines */}
        {gridLines.map((pct) => {
          const y = chartHeight - padding.bottom - pct * yScale
          return (
            <g key={pct}>
              <line
                x1={padding.left}
                y1={y}
                x2={chartWidth - padding.right}
                y2={y}
                stroke="currentColor"
                strokeOpacity={0.1}
                strokeDasharray="4,4"
              />
              <text
                x={padding.left - 8}
                y={y + 4}
                textAnchor="end"
                className="fill-current text-secondary"
                fontSize="11"
              >
                {pct}%
              </text>
            </g>
          )
        })}

        {/* Gradient definition */}
        <defs>
          <linearGradient id="agentGradient" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#4146DB" stopOpacity={0.3} />
            <stop offset="100%" stopColor="#4146DB" stopOpacity={0.05} />
          </linearGradient>
        </defs>

        {/* Area fill */}
        <path d={areaPath} fill="url(#agentGradient)" />

        {/* Line */}
        <path
          d={linePath}
          fill="none"
          stroke="#4146DB"
          strokeWidth={2.5}
          strokeLinecap="round"
          strokeLinejoin="round"
        />

        {/* Data points and labels */}
        {points.map((p) => (
          <g key={p.year}>
            {/* Point */}
            <circle
              cx={p.x}
              cy={p.y}
              r={5}
              fill="var(--color-bg-primary)"
              stroke="#4146DB"
              strokeWidth={2}
            />

            {/* Percentage label above point */}
            <text
              x={p.x}
              y={p.y - 12}
              textAnchor="middle"
              className="fill-current text-primary font-semibold"
              fontSize="12"
            >
              {p.percentage.toFixed(1)}%
            </text>

            {/* Year label below */}
            <text
              x={p.x}
              y={chartHeight - padding.bottom + 18}
              textAnchor="middle"
              className="fill-current text-secondary"
              fontSize="12"
            >
              {p.year}
            </text>
          </g>
        ))}

        {/* Y-axis label */}
        <text
          x={14}
          y={chartHeight / 2}
          textAnchor="middle"
          className="fill-current text-secondary"
          fontSize="10"
          transform={`rotate(-90, 14, ${chartHeight / 2})`}
        >
          % of YC Startups Building Agents
        </text>
      </svg>
    </div>
  )
}

export default GrowthChart
